"use client";

// https://github.com/mxkaske/mxkaske.dev/blob/main/components/craft/fancy-multi-select.tsx

import { Command as CommandPrimitive } from "cmdk";
import { XIcon } from "lucide-react";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";

type SelectValue = { value: string; label: string; image?: string };

export function FancyMultiSelect({
	name,
	defaultValue = [],
	allSelectables,
	isLoading,
	onValueChange,
	value,
	placeholder,
}: {
	name?: string;
	defaultValue?: SelectValue[];
	allSelectables: SelectValue[];
	isLoading?: boolean;
	onValueChange?: (value: SelectValue[]) => void;
	value?: SelectValue[];
	placeholder?: string;
}) {
	const inputRef = useRef<HTMLInputElement>(null);
	const [open, setOpen] = useState(false);
	const [selectedValues, setSelectedValues] = useState<SelectValue[]>(value ?? defaultValue ?? []);
	const [inputValue, setInputValue] = useState("");

	// console.log({ selectedValues, defaultValue });

	useEffect(() => {
		if (value) {
			setSelectedValues(value);
		}
	}, [value]);

	const handleUnselect = (selectValue: SelectValue) => {
		const newSelected = selectedValues.filter((s) => s.value !== selectValue.value);
		setSelectedValues(newSelected);
		onValueChange?.(newSelected);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		const input = inputRef.current;
		if (!input) {
			return;
		}
		if (e.key === "Delete" || e.key === "Backspace") {
			if (input.value === "") {
				const newSelected = selectedValues.slice(0, -1);
				setSelectedValues(newSelected);
				onValueChange?.(newSelected);
			}
		}
		// This is not a default behaviour of the <input /> field
		if (e.key === "Escape") {
			input.blur();
		}
	};

	const selectables = allSelectables.filter((selectValue) =>
		selectedValues.every((s) => s.value !== selectValue.value),
	);

	return (
		<Command onKeyDown={handleKeyDown} className="overflow-visible bg-transparent">
			<div className="group rounded-md border border-input px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
				<div className="flex flex-wrap gap-2">
					{selectedValues.map((selectedValue) => {
						return (
							<Badge
								key={selectedValue.value}
								variant="secondary"
								className="flex items-center gap-2 pl-1 pr-2 py-1 h-8"
							>
								<input type="hidden" name={name} value={selectedValue.value} />
								{selectedValue.image && (
									<div className="relative size-10 rounded overflow-hidden bg-gray-100 flex-shrink-0">
										<Image
											src={selectedValue.image}
											alt={selectedValue.label}
											width={24}
											height={24}
											className="object-cover w-full h-full"
											sizes="24px"
										/>
									</div>
								)}
								{!selectedValue.image && (
									<div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
										<span className="text-xs text-gray-500 font-medium">
											{selectedValue.label.charAt(0).toUpperCase()}
										</span>
									</div>
								)}
								<span className="text-sm font-medium truncate max-w-[120px]">{selectedValue.label}</span>

								{/* do not use `button` here because it will be autofocused for some reason */}
								<div
									tabIndex={0}
									role="button"
									className="ml-1 rounded-full outline-hidden ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2 hover:bg-gray-200 p-0.5 transition-colors"
									onMouseDown={(e) => {
										e.preventDefault();
										e.stopPropagation();
									}}
									onKeyDown={(e) => {
										if (e.key === "Enter") {
											handleUnselect(selectedValue);
										}
									}}
									onClick={() => handleUnselect(selectedValue)}
								>
									<XIcon className="h-3 w-3 text-muted-foreground hover:text-foreground" />
								</div>
							</Badge>
						);
					})}
					{/* Avoid having the "Search" Icon */}
					<CommandPrimitive.Input
						ref={inputRef}
						value={inputValue}
						disabled={allSelectables.length === 0}
						onValueChange={setInputValue}
						onBlur={() => setOpen(false)}
						onFocus={() => setOpen(true)}
						placeholder={allSelectables.length === 0 && !isLoading ? "No collections found" : placeholder}
						className="ml-2 flex-1 bg-transparent outline-hidden placeholder:text-muted-foreground"
					/>
				</div>
			</div>
			<div className="relative mt-2">
				<CommandList>
					{open && selectables.length > 0 ? (
						<div className="absolute top-0 z-50 w-full rounded-md border bg-popover text-popover-foreground shadow-md outline-hidden animate-in max-h-60 overflow-auto">
							<CommandGroup className="h-full overflow-auto">
								{selectables.map((selectable) => {
									return (
										<CommandItem
											key={selectable.value}
											onMouseDown={(e) => {
												e.preventDefault();
												e.stopPropagation();

												setInputValue("");
												const newSelected = [...selectedValues, selectable];
												setSelectedValues(newSelected);
												onValueChange?.(newSelected);
											}}
											className={cn("cursor-pointer flex items-center gap-3 py-2")}
										>
											{selectable.image && (
												<div className="relative w-8 h-8 rounded overflow-hidden bg-gray-100 flex-shrink-0">
													<Image
														src={selectable.image}
														alt={selectable.label}
														width={32}
														height={32}
														className="object-cover w-full h-full"
														sizes="32px"
													/>
												</div>
											)}
											{!selectable.image && (
												<div className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
													<span className="text-sm text-gray-500 font-medium">
														{selectable.label.charAt(0).toUpperCase()}
													</span>
												</div>
											)}
											<div className="flex flex-col flex-1 min-w-0">
												<span className="text-sm font-medium text-foreground truncate">
													{selectable.label}
												</span>
											</div>
										</CommandItem>
									);
								})}
							</CommandGroup>
						</div>
					) : null}
				</CommandList>
			</div>
		</Command>
	);
}
